import type { AIModel, AIPrompt, AIPromptMapping } from '@/types/ai';
import type { PromptListRow } from '@/types/ui/PromptManager';
import type { LabelValue } from '@/types/label-value';

const DRAFT = 'draft';

const isDraft = (prompt: AIPrompt) => prompt.version.toLowerCase() === DRAFT;

// Draft first, then published versions newest to oldest — the order the version
// dropdown already uses, so both views agree on which version is "current".
const byVersionDescending = (a: AIPrompt, b: AIPrompt) => {
  if (isDraft(a) && !isDraft(b)) return -1;
  if (!isDraft(a) && isDraft(b)) return 1;
  if (isDraft(a) && isDraft(b)) {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  }
  return (parseInt(b.version) || 0) - (parseInt(a.version) || 0);
};

const groupByPromptId = (versions: AIPrompt[]): Map<string, AIPrompt[]> => {
  const groups = new Map<string, AIPrompt[]>();
  for (const version of versions) {
    const key = String(version.promptId) || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(version);
  }
  groups.forEach((group) => group.sort(byVersionDescending));
  return groups;
};

// Models come and go; a prompt pointing at one that is gone still has to show
// something, so fall back to the tail of the ARN as before.
const modelNameFor = (modelArn: string | undefined, modelNames: Map<string, string>) => {
  if (!modelArn) return '—';
  return modelNames.get(modelArn) ?? modelArn.split('/').pop() ?? '—';
};

const coverageNamesFor = (
  promptId: number | string,
  mappings: AIPromptMapping[],
  coverageTypeNames: Map<number, string>,
): string[] => {
  const names = new Set<string>();
  for (const mapping of mappings) {
    if (mapping.promptId === Number(promptId)) {
      names.add(coverageTypeNames.get(mapping.coverageTypeId) ?? 'Unknown Coverage');
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
};

/**
 * Collapses the one-row-per-version payload from getAllPrompts into one row per
 * prompt for the list view, resolving coverage-type names from prompt mappings.
 *
 * modifiedBy has no true source — AIPrompts has no UpdatedBy column — so it
 * reads as "last published by", falling back to who created the prompt.
 *
 * status means "has this prompt ever been published". Every prompt keeps a
 * permanent DRAFT version — publishing copies it to a new numbered version
 * rather than consuming it — so a draft's presence says nothing.
 */
export const buildPromptListRows = (
  versions: AIPrompt[],
  mappings: AIPromptMapping[] = [],
  coverageTypes: LabelValue[] = [],
  models: AIModel[] = [],
): PromptListRow[] => {
  const coverageTypeNames = new Map(coverageTypes.map((type) => [type.value, type.label]));
  const modelNames = new Map(models.map((model) => [model.arn, model.name]));

  const rows: PromptListRow[] = [];
  for (const group of groupByPromptId(versions).values()) {
    const draft = group.find(isDraft);
    const newestPublished = group.find((version) => !isDraft(version));
    const representative = newestPublished ?? group[0];
    if (!representative) continue;

    rows.push({
      promptKey: draft?.name || group[0].name || 'Unnamed',
      promptId: representative.promptId,
      name: draft?.name || group[0].name || 'Unnamed',
      designation: representative.designation,
      status: newestPublished ? 'Published' : 'Draft',
      modelArn: representative.modelArn,
      modelName: modelNameFor(representative.modelArn, modelNames),
      versionCount: group.length,
      lastModified: representative.updatedAt,
      modifiedBy: representative.publishedByName || representative.createdByName || '—',
      coverageTypes: coverageNamesFor(representative.promptId, mappings, coverageTypeNames),
      representativeVersion: representative.version,
    });
  }

  return rows.sort((a, b) => a.name.localeCompare(b.name));
};
